import {
  ServicePageModalType,
  useServicePageModalContext,
} from 'restaurantAdmin/context/ServicePageModalContext';
import type { HostFloorPlanTable } from 'restaurantAdmin/floorPlans/apiHelpers';
import {
  changeReservationSeat,
  seatReservation as seatReservationApi,
} from 'restaurantAdmin/reservations/apiHelpers';
import { errorToast } from '@components/toasts/Toasts';
import { ApiError } from '@shared/api/errors';
import { reportAppError } from '@shared/reportAppError';
import { getFullName } from '@shared/utils/formatName';
import { intersection } from 'lodash-es';
import type { ServiceReservation } from '../apiHelpers';
import { useReservationServiceContext } from '../state/ReservationServiceContext';
import { useServiceSearchParams } from './useServiceSearchParams';

export const useSeatReservation = ({
  restaurantId,
}: {
  restaurantId: string;
}) => {
  const { exitSeatMode } = useServiceSearchParams();
  const { closeSidePanelSheet } = useReservationServiceContext();
  const { openModal } = useServicePageModalContext();

  const isSeatingAtTableChosenDuringBooking = (
    floorPlanTables: HostFloorPlanTable[],
    reservationOccupant: ServiceReservation,
  ): boolean => {
    // If no tables were chosen at booking then any table will satisfy this condition.
    if (!reservationOccupant.wasTableChosenAtTimeOfBooking) return true;

    const seatingListingIds = floorPlanTables.flatMap((floorPlanTable) =>
      floorPlanTable.listings.map((l) => l.id),
    );

    const preselectedTableListingIds =
      reservationOccupant?.tableAssignments?.map(
        (assignment) => assignment.listingId,
      );

    return (
      intersection(seatingListingIds, preselectedTableListingIds).length > 0
    );
  };

  const openSeatReservationOutsideListingWarningModal = (
    reservationOccupant: ServiceReservation,
    handleConfirm: () => Promise<void>,
  ): void => {
    const guestName = getFullName(
      reservationOccupant.guest.firstName,
      reservationOccupant.guest.lastName,
    );
    openModal(ServicePageModalType.SeatOutsideListingConfirmation, {
      handleConfirm,
      guestName,
    });
  };

  const seatReservation = async (
    floorPlanTables: HostFloorPlanTable[],
    reservationOccupant: ServiceReservation,
  ): Promise<void> => {
    try {
      const call = () =>
        seatReservationApi({
          restaurantId,
          reservationId: reservationOccupant.id,
          floorPlanTableIds: floorPlanTables.map((table) => table.id),
        });

      if (
        isSeatingAtTableChosenDuringBooking(
          floorPlanTables,
          reservationOccupant,
        )
      ) {
        await call();
      } else {
        openSeatReservationOutsideListingWarningModal(
          reservationOccupant,
          call,
        );
      }

      closeSidePanelSheet();
      exitSeatMode();
    } catch (error) {
      if (error instanceof ApiError) {
        errorToast({ message: error.message });
      } else {
        errorToast({
          message: 'Failed to seat reservation. Please try again.',
        });
      }
      reportAppError(error);
    }
  };

  const reseatReservation = async (
    floorPlanTables: HostFloorPlanTable[],
    reservationOccupant: ServiceReservation,
  ): Promise<void> => {
    try {
      const call = () =>
        changeReservationSeat({
          restaurantId,
          reservationId: reservationOccupant.id,
          floorPlanTableIds: floorPlanTables.map((table) => table.id),
        });

      if (
        isSeatingAtTableChosenDuringBooking(
          floorPlanTables,
          reservationOccupant,
        )
      ) {
        await call();
      } else {
        openSeatReservationOutsideListingWarningModal(
          reservationOccupant,
          call,
        );
      }

      closeSidePanelSheet();
      exitSeatMode();
    } catch (error) {
      if (error instanceof ApiError) {
        errorToast({ message: error.message });
      } else {
        errorToast({
          message: 'Failed to seat reservation. Please try again.',
        });
      }
      reportAppError(error);
    }
  };

  return {
    seatReservation,
    reseatReservation,
  };
};
